/**
 * TrustScopedLanding — generic "you tried to visit a tenant-wide page that
 * actually requires a trust context" landing.
 *
 * Replaces the legacy "redirect to /app/trusts/vandermerwe-family-trust/X"
 * fixture pattern that was scattered across 8 routes from the demo era.
 *
 * Behaviour:
 *   - 0 trusts → empty state with help copy
 *   - 1 trust → fast-path redirect to /app/trusts/$id/<suffix>
 *   - 2+ trusts → picker; click navigates to the per-trust URL
 *
 * Usage:
 *   <TrustScopedLanding
 *     suffix="audit"
 *     eyebrow="Audit log"
 *     title={<em>Audit log.</em>}
 *     emptyHelp="Audit events appear here once activity begins on your trust."
 *   />
 */

import { useEffect, useMemo, type ReactNode } from 'react';
import { displayLegalName } from '../../lib/format/legal-name';
import { ScreenShell } from './screen-shell';
import { ConsoleBand } from '../admin/console-band';
import { StateCard } from '../state-card';
import { EntityRow } from '../entity-row';
import { useTrusts, type TrustSummary } from '../../lib/api';
import { formatRand } from '../../lib/fmt-r';
import { mapError } from '../../lib/error-messages';

export interface TrustScopedLandingProps {
  /** The path segment to append after /app/trusts/$id/. e.g. 'distributions'. */
  suffix:
    | 'audit'
    | 'beneficiaries'
    | 'beneficial-ownership'
    | 'distributions'
    | 'documents'
    | 'resolutions'
    | 'tax'
    | '';
  eyebrow: string;
  /** ScreenShell title (ReactNode — accepts JSX like <em>). */
  title: ReactNode;
  /** Optional bottom-note rendered under the picker / empty state. */
  emptyHelp?: ReactNode;
  activeNav?: 'trusts' | 'audit' | 'documents';
}

// 2026-05-20 (Batch 8 / F02-P-074): collapsed onto the shared formatRand
// from lib/fmt-r so every Rand cell uses the same narrow-space thousands
// separator and NBSP between R and the number.
function fmtR(n: number) {
  return formatRand(n);
}

const SUFFIX_NOUN: Record<TrustScopedLandingProps['suffix'], string> = {
  audit: 'audit log',
  beneficiaries: 'beneficiaries register',
  'beneficial-ownership': 'beneficial-ownership chart',
  distributions: 'distribution history',
  documents: 'documents library',
  resolutions: 'resolutions list',
  tax: 'tax certificates',
  '': 'trust',
};

function resolvedHref(trustId: string, suffix: TrustScopedLandingProps['suffix']): string {
  const id = encodeURIComponent(trustId);
  return suffix === '' ? `/app/trusts/${id}` : `/app/trusts/${id}/${suffix}`;
}

export function TrustScopedLanding({
  suffix,
  eyebrow,
  title,
  emptyHelp,
  activeNav = 'trusts',
}: TrustScopedLandingProps) {
  const { data, isLoading, isError, error } = useTrusts();
  // Memoise the trusts array — the `data?.trusts ?? []` fallback constructs
  // a fresh empty array each render, which would otherwise cause the
  // effect dependency identity to flip every commit (react-hooks/
  // exhaustive-deps warning). The memo keys off `data` itself so we
  // re-evaluate only when the query payload changes.
  const trusts = useMemo<TrustSummary[]>(() => data?.trusts ?? [], [data]);

  // Single-trust fast path — saves a click on the common case. Full nav
  // (window.location.replace) rather than client-side route, because the
  // typed Link/useNavigate APIs cannot accept a string-keyed dynamic route;
  // these landing routes are rarely-hit so the extra nav is acceptable.
  useEffect(() => {
    if (!isLoading && !isError && trusts.length === 1 && trusts[0]) {
      window.location.replace(resolvedHref(trusts[0].id, suffix));
    }
  }, [isLoading, isError, trusts, suffix]);

  const noun = SUFFIX_NOUN[suffix];
  const sub = isLoading
    ? 'Loading…'
    : isError
      ? 'Failed to load'
      : trusts.length === 0
        ? 'No trusts yet'
        : trusts.length === 1
          ? 'Redirecting…'
          : `Pick a trust: ${trusts.length} under your administration`;

  // The default fallback text kept its original two sentences, split into a
  // headline (what would be here) and a detail (when it appears), which is
  // the shape design gate A1 asks for. A screen that passes its own
  // `emptyHelp` paragraph keeps that paragraph whole, as the detail line.
  const emptyHeadline = 'Nothing here yet';
  const emptyDetail =
    emptyHelp ??
    `You don't have any trusts under your administration yet. The ${noun} appears here once a trust is onboarded.`;

  return (
    <ScreenShell activeNav={activeNav} eyebrow={eyebrow} title={title} sub={sub}>
      {isLoading ? (
        <StateCard
          variant="loading"
          testId="landing-loading"
          label={eyebrow}
          headline="Loading trusts…"
        />
      ) : isError ? (
        <StateCard
          variant="error"
          testId="landing-error"
          label={eyebrow}
          headline={mapError(error, 'load')}
        />
      ) : trusts.length === 0 ? (
        <StateCard
          variant="empty"
          testId="landing-empty"
          label={eyebrow}
          headline={emptyHeadline}
          detail={emptyDetail}
        />
      ) : trusts.length === 1 ? (
        <StateCard
          variant="loading"
          testId="landing-redirect"
          label={eyebrow}
          headline={`Opening your trust's ${noun}…`}
        />
      ) : (
        <ConsoleBand id="landing-picker" title="Pick a trust">
          <div className="card">
            {trusts.map((t) => (
              <TrustPickerRow key={t.id} trust={t} suffix={suffix} />
            ))}
          </div>
        </ConsoleBand>
      )}
    </ScreenShell>
  );
}

function TrustPickerRow({
  trust,
  suffix,
}: {
  trust: TrustSummary;
  suffix: TrustScopedLandingProps['suffix'];
}) {
  return (
    <EntityRow
      testId={`trust-picker-${trust.id}`}
      to={resolvedHref(trust.id, suffix)}
      name={displayLegalName(trust.name)}
      description={<span className="mono">{trust.registration}</span>}
      tone="routine"
      // AUM is the `financials` surface, which the API omits for a caller who
      // may not read it. Omit the value rather than print "R 0" — a
      // beneficiary's trust does not become worthless because they cannot be
      // shown the figure. Rendered via `actions` (a plain value, not a
      // button): the row has no chip-worthy deadline to state, and `urgency`
      // exists to state one, not to carry an amount.
      actions={
        trust.totalValue !== undefined ? (
          // Roman, not italic: an amount is not a named thing (italic is
          // reserved for entities — the rule design gate D2 and the 3a model
          // both enforce).
          <span
            style={{
              fontWeight: 500,
              fontSize: 15,
              color: 'var(--ink)',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {fmtR(trust.totalValue)}
          </span>
        ) : undefined
      }
    />
  );
}
